import { buildEmptyTM, buildEmptyCleaning } from "../../lib/tmEngine";

export const BLANK = {
    mode: "ai",            // "ai" = AI chat estimator (default), "rom" = sq-ft engine, "tm" = Excel-style line item, "cleaning" = commercial cleaning
    tm: buildEmptyTM(),    // T&M line items
    cleaning: buildEmptyCleaning(), // standalone commercial cleaning quote
    propertyScale: "",     // "residential" | "commercial" — set by entry point; falls back to derived-from-propertyType
    propertyType: "",
    lossType: "",
    secondaryLossType: "",
    secondaryLossOther: "",
    projectStartDate: new Date().toISOString().slice(0, 10),
    sqft: "",
    sqftOverride: false,
    rooms: [],
    siteConditions: [],
    stories: "",
    unitCount: 1,
    waterCategory: "",
    waterClass: "",
    scope: "",
    hoursSinceLoss: "",
    includeRecon: false,
    primeArea: false,
    emergency: false,
    farJob: false,
    outOfState: false,
    consumablesPct: 0.095,
    otHoursPerDay: 0,
    clientId: "",
    insurerId: "",
    claimNumber: "",
    address: "",
};

// Inputs needed before the engine produces a meaningful result.

export function hasMinimumInputs(inp) {
    return !!(inp && inp.lossType && Number(inp.sqft) > 0 && inp.scope);
}

export const ARITH_RE = /[+\-*/()=]/;
export function evalArithmetic(raw) {
    const s = String(raw).trim().replace(/^=/, "");
    if (s === "") return { ok: true, value: "" };
    if (!/^[\d+\-*/().\s]+$/.test(s)) return { ok: false, err: "Only digits and + − × ÷ ( ) allowed" };
    try {
        // eslint-disable-next-line no-new-func
        const out = Function(`"use strict"; return (${s});`)();
        if (typeof out !== "number" || !isFinite(out)) return { ok: false, err: "Invalid formula" };
        return { ok: true, value: Math.max(0, Math.round(out * 100) / 100) };
    } catch {
        return { ok: false, err: "Invalid formula" };
    }
}

/* Inline editable cell. Accepts plain numbers (live update) OR Excel-style
 * arithmetic like (5*30)+12 / =50*2, evaluated on Enter or blur. Used across
 * every ROM editable cell (labour, equipment, subs, markup, misc). */

export const STATUS_LABELS = { not_started: "Not Started", in_progress: "In Progress", complete: "Complete" };

export function addDaysISO(iso, n) {
    if (!iso) return null;
    const d = new Date(`${iso}T00:00:00`);
    if (isNaN(d.getTime())) return null;
    d.setDate(d.getDate() + (Number(n) || 0));
    return d;
}

export function fmtDateShort(iso) {
    const d = typeof iso === "string" ? addDaysISO(iso, 0) : iso;
    if (!d || isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short" });
}

export function makeRef() {
    const d = new Date();
    return `R-${d.getFullYear()}${String(d.getMonth()+1).padStart(2,"0")}${String(d.getDate()).padStart(2,"0")}-${Math.floor(Math.random()*9000+1000)}`;
}

/* Great-circle distance between two lat/lng points in miles. */

export function haversineMiles(lat1, lng1, lat2, lng2) {
    const R = 3958.7613;            // mean earth radius in miles
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 +
              Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
}

export const DEMO_LEVEL_LABEL = { "": "No demo / in-place", "2ft": "2 ft flood cut", "4ft": "4 ft cut", full_gut: "Floor-to-ceiling / full gut" };

/* Groups rooms by floor/level for the read-only scope summary shown in the ROM
 * editor, PDF and adjuster share link — office keys this straight into Xactimate. */
export function groupRoomsByFloor(rooms) {
    const list = Array.isArray(rooms) ? rooms : [];
    const byFloor = new Map();
    list.forEach((r) => {
        const floor = (r.floor || "").trim() || "Unspecified floor / area";
        if (!byFloor.has(floor)) byFloor.set(floor, []);
        byFloor.get(floor).push(r);
    });
    return Array.from(byFloor.entries()).map(([floor, items]) => ({
        floor,
        rooms: items,
        sqft: items.reduce((s, r) => s + (Number(r.sqft) || 0), 0),
    }));
}

/* Returns true/false if confident, null if unknown.
 * Prime Area per the rate schedule: Boston, Chicago, Denver, Minneapolis, NYC,
 * Washington DC (25-mi radius); all of CA, WA, OR, AK, HI; declared CAT zones. */

export function isPrimeAreaLocation(parts) {
    if (!parts) return null;
    const state = (parts.state || "").toUpperCase();
    const city = (parts.city || "").toLowerCase();
    if (["CA", "WA", "OR", "AK", "HI"].includes(state)) return true;
    if (state === "DC") return true;
    if (state === "MA" && city.includes("boston")) return true;
    if (state === "NY" && /(new york|manhattan|brooklyn|bronx|queens|staten island)/.test(city)) return true;
    if (state === "IL" && city.includes("chicago")) return true;
    if (state === "CO" && city.includes("denver")) return true;
    if (state === "MN" && /(minneapolis|st\.?\s?paul|saint paul)/.test(city)) return true;
    if (city.includes("washington") && (state === "DC" || state === "")) return true;
    if (state) return false;
    return null;
}
